import React, { SyntheticEvent, useState, useEffect, useCallback } from 'react';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Link, Stack, Typography, Box } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import useAuthService from 'hooks/useAuthService';
import routePaths from 'routes/routePaths';
import { IApiError } from 'models';
import useToast from 'hooks/useToast';
import OtpInput from './OtpInput';
import { IOtpSession, OTP_SESSION_KEY } from './verifyOtp.model';

const DEFAULT_COOLDOWN = 60;

export default function VerifyOtpForm() {
  const history = useHistory();
  const { verifyOtp, resendOtp } = useAuthService();
  const { showToast } = useToast();
  const [otp, setOtp] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [otpError, setOtpError] = useState(false);

  const { t } = useTranslation(['verifyOtp', 'labels']);

  const otpSession: IOtpSession | null = (() => {
    try {
      return JSON.parse(localStorage.getItem(OTP_SESSION_KEY) || '');
    } catch {
      return null;
    }
  })();

  const [cooldown, setCooldown] = useState(DEFAULT_COOLDOWN);

  useEffect(() => {
    if (!otpSession?.mfaSessionId) {
      history.replace(routePaths.login);
    }
  }, [otpSession, history]);

  useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldown]);

  const handleOtpChange = useCallback((value: string) => {
    setOtp(value);
    setOtpError(false);
  }, []);

  useEffect(() => {
    if (otp.length === 6 && otpSession?.mfaSessionId) {
      handleSubmit();
    }
  }, [otp]);

  const handleSubmit = (e?: SyntheticEvent) => {
    e?.preventDefault();

    if (otp.length !== 6) {
      setOtpError(true);
      return;
    }

    if (!otpSession?.mfaSessionId) {
      showToast({ message: t('verifyOtp:sessionExpired'), type: 'error' });
      history.replace(routePaths.login);
      return;
    }

    setIsSubmitting(true);
    verifyOtp({ mfaSessionId: otpSession.mfaSessionId, otp })
      .then((res) => {
        localStorage.removeItem(OTP_SESSION_KEY);
        if (res?.data?.token) {
          if (res.data.user?.firstLogin) {
            history.push(routePaths.changePassword);
          } else {
            history.push(routePaths.dashboard);
          }
        }
      })
      .catch((err: IApiError) => {
        showToast({ message: err.description, type: 'error' });
        setOtpError(true);
        setOtp('');
        setIsSubmitting(false);
      });
  };

  const handleResend = () => {
    if (cooldown > 0 || !otpSession?.mfaSessionId) return;

    setIsResending(true);
    resendOtp({ mfaSessionId: otpSession.mfaSessionId })
      .then((res) => {
        showToast({ message: res.description || t('verifyOtp:otpResent'), type: 'success' });
        setCooldown(DEFAULT_COOLDOWN);
        setOtp('');
        setOtpError(false);
      })
      .catch((err: IApiError) => {
        showToast({ message: err.description, type: 'error' });
      })
      .finally(() => setIsResending(false));
  };

  const handleBackToLogin = () => {
    localStorage.removeItem(OTP_SESSION_KEY);
  };

  return (
    <form autoComplete="off" noValidate onSubmit={handleSubmit}>
      <Stack spacing={3}>
        {(otpSession?.maskedPhone || otpSession?.maskedEmail) && (
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {t('verifyOtp:otpSentTo')} <strong>{otpSession.maskedPhone || otpSession.maskedEmail}</strong>
            {otpSession.otpDeliveryMethod && ` (${otpSession.otpDeliveryMethod})`}
          </Typography>
        )}

        <Box sx={{ py: 2 }}>
          <OtpInput value={otp} onChange={handleOtpChange} error={otpError} disabled={isSubmitting} />
        </Box>

        {otpError && (
          <Typography variant="caption" color="error" textAlign="center">
            {t('verifyOtp:invalidOtp')}
          </Typography>
        )}
      </Stack>

      <Stack alignItems="center" justifyContent="center" sx={{ my: 2 }}>
        <LoadingButton
          variant="text"
          onClick={handleResend}
          disabled={cooldown > 0}
          loading={isResending}
          sx={{ textTransform: 'none' }}
        >
          {cooldown > 0 ? `${t('verifyOtp:resendIn')} ${cooldown}s` : t('verifyOtp:resendOtp')}
        </LoadingButton>
      </Stack>

      <LoadingButton
        fullWidth
        size="large"
        type="submit"
        variant="contained"
        loading={isSubmitting}
        disabled={otp.length !== 6}
      >
        {t('verifyOtp:verify')}
      </LoadingButton>

      <Stack alignItems="center" justifyContent="center" sx={{ mt: 3 }}>
        <Link component={RouterLink} variant="subtitle2" to={routePaths.login} onClick={handleBackToLogin}>
          {t('verifyOtp:backToLogin')}
        </Link>
      </Stack>
    </form>
  );
}
